import { useMutation } from '@tanstack/react-query';
import { authApiService } from '../api/auth.api';
import { normalizeApiError, type NormalizedApiError } from '@/lib/api/apiError';
import type { RegisterPayload, VerifyOtpPayload } from '../types/auth.types';

/**
 * Create the account on the auth server. The verification code is NOT mailed
 * here: it goes out at the first (hosted) login, after which `ProtectedRoute`
 * sends the user to the verify-email screen.
 */
export function useRegister() {
  return useMutation<{ id?: number; email?: string; message?: string }, NormalizedApiError, RegisterPayload>({
    mutationKey: ['auth', 'register'],
    mutationFn: async (payload) => {
      try {
        return await authApiService.register(payload);
      } catch (error) {
        throw normalizeApiError(error);
      }
    },
  });
}

/** Confirm the emailed code for the signed-in account (post-login). */
export function useVerifyOtp() {
  return useMutation<{ message?: string }, NormalizedApiError, VerifyOtpPayload>({
    mutationKey: ['auth', 'verify-otp'],
    mutationFn: async (payload) => {
      try {
        return await authApiService.verifyOtp(payload);
      } catch (error) {
        const normalized = normalizeApiError(error);
        // The auth server answers 400 {error: 'Invalid or expired OTP'} — no
        // `message`, and English. Map it to a localizable key.
        throw normalized.status === 400 ? { ...normalized, message: 'errors.otp_invalid' } : normalized;
      }
    },
  });
}

/** Mail a fresh code to the signed-in account. */
export function useResendOtp() {
  return useMutation<{ message?: string }, NormalizedApiError, string>({
    mutationKey: ['auth', 'resend-otp'],
    mutationFn: async (email) => {
      try {
        return await authApiService.resendOtp(email);
      } catch (error) {
        const normalized = normalizeApiError(error);
        // 400 = already verified / unknown address (English server text).
        throw normalized.status === 400 ? { ...normalized, message: 'errors.otp_resend_failed' } : normalized;
      }
    },
  });
}
